/**
 * InviteDmTemplate
 *
 * Pre-formatted DM message a super-admin can copy and paste straight into
 * Instagram / WhatsApp / Telegram. Two locales (es/en) and two destinations
 * (exclusive_ar with VIP urgency vs exclusive international).
 *
 * Goal: zero copy/paste friction. The operator generates a 60s burst link,
 * clicks "Copiar mensaje" and pastes a complete, urgency-loaded DM.
 */

import { useState } from "react";
import { Check, MessageSquare } from "lucide-react";

interface Props {
  link: string;
  /** Approximate time-to-live so the message text mentions urgency. */
  expirationSeconds: number;
  destination: "exclusive_ar" | "exclusive" | "invitacion_miembro";
  className?: string;
}

function formatTimeWindow(seconds: number): string {
  if (seconds < 60) return `${seconds} segundos`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} minutos`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} hora${seconds >= 7200 ? "s" : ""}`;
  return `${Math.round(seconds / 86400)} día${seconds >= 172800 ? "s" : ""}`;
}

function buildMessage(opts: {
  link: string;
  expirationSeconds: number;
  destination: "exclusive_ar" | "exclusive" | "invitacion_miembro";
}): string {
  const window = formatTimeWindow(opts.expirationSeconds);

  if (opts.destination === "exclusive_ar") {
    // AR — Spanish, casual, urgency-loaded
    return [
      "🔒 Te dejo este acceso por solo " + window + ".",
      "",
      "Es un link directo al video VIP — solo para vos.",
      "",
      opts.link,
      "",
      "Si no entrás antes que se cumpla el tiempo, el link se cierra y no podés recuperarlo.",
    ].join("\n");
  }

  if (opts.destination === "invitacion_miembro") {
    // DualCTA VIP membership landing — Spanish
    return [
      "🌟 Te dejo este link exclusivo por " + window + ".",
      "",
      "Desde ahí podés unirte directamente a la membresía VIP — video incluido.",
      "",
      opts.link,
      "",
      "El link expira en " + window + ", así que no lo dejes para después.",
    ].join("\n");
  }

  // International — English, similar tone
  return [
    "🔒 I'm giving you " + window + " of access.",
    "",
    "Direct link to the VIP video — only for you.",
    "",
    opts.link,
    "",
    "If you don't get in before the timer ends, the link closes and you can't recover it.",
  ].join("\n");
}

export default function InviteDmTemplate({ link, expirationSeconds, destination, className = "" }: Props) {
  const [copied, setCopied] = useState(false);
  const message = buildMessage({ link, expirationSeconds, destination });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard not available */ }
  };

  return (
    <div className={`rounded-lg border border-zinc-800 bg-zinc-950/80 p-3 ${className}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
          Mensaje listo para DM
        </span>
        <button
          onClick={handleCopy}
          className="inline-flex items-center gap-1 rounded-md bg-amber-500 px-2 py-1 text-xs font-medium text-black hover:bg-amber-600 transition-colors"
        >
          {copied ? <Check className="h-3 w-3" /> : <MessageSquare className="h-3 w-3" />}
          {copied ? "Copiado" : "Copiar mensaje"}
        </button>
      </div>
      <pre className="whitespace-pre-wrap break-words font-ios text-[11px] leading-relaxed text-zinc-300">
        {message}
      </pre>
    </div>
  );
}
